import React, { useRef, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { exportPostConsultWorkbook } from "./PostConsultExcel";
import { db } from "../../../../firebase";
import {
  getPostConsultSummaryNodes, POST_CONSULT_STATUSES, summarizePostConsult,
} from "./PostConsultSummaryUtils";

const StatusCounts = ({ rows }) => (
  <dl className="d-flex flex-wrap mb-2" style={{ gap: "4px 12px" }}>
    {rows.map(({ status, count }) => (
      <div key={status} className="d-inline-flex align-items-baseline">
        <dt className="font-weight-normal mr-1">{status}:</dt>
        <dd className="font-weight-bold mb-0">{count}</dd>
      </div>
    ))}
  </dl>
);

const AssignmentCounts = ({ title, rows, showStatuses }) => (
  <div className="col-md-6">
    <h6>{title}</h6>
    <table className="table table-sm mb-3" aria-label={`${title} counts by current status`}>
      <thead><tr><th scope="col">Name</th><th scope="col" className="text-right">Cases</th></tr></thead>
      <tbody>{rows.map(({ name, count, statuses }) => (
        <tr key={name}><td><strong>{name}</strong>{showStatuses && <div className="small text-muted mt-1"><StatusCounts rows={statuses} /></div>}</td><td className="text-right">{count}</td></tr>
      ))}{!rows.length && <tr><td colSpan="2">No cases</td></tr>}</tbody>
    </table>
  </div>
);

const PostConsultSummary = ({ currentUser, allowedNodes }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [nodeId, setNodeId] = useState("");
  const [customer, setCustomer] = useState("");
  const [mode, setMode] = useState("short");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const busy = useRef(false);
  const nodes = getPostConsultSummaryNodes(currentUser, allowedNodes);
  if (!nodes.length) return null;
  const selectedNodes = nodeId ? nodes.filter((node) => node.id === nodeId) : nodes;
  const customers = Array.from(new Set(selectedNodes.reduce((all, node) => all.concat(node.customers || []), [])))
    .sort((a, b) => a.localeCompare(b, "th"));

  const generate = async () => {
    if (busy.current) return;
    if (!startDate || !endDate || startDate > endDate || !selectedNodes.length) {
      setError("Select a valid latest sleep study date range.");
      return;
    }
    busy.current = true;
    setLoading(true);
    setSummary(null);
    setError("");
    try {
      const bookings = [];
      // Read only authorized node collections; no per-patient enrichment is needed.
      for (let index = 0; index < selectedNodes.length; index += 4) {
        setProgress(`Loading nodes ${index + 1}–${Math.min(index + 4, selectedNodes.length)} of ${selectedNodes.length}...`);
        const snapshots = await Promise.all(selectedNodes.slice(index, index + 4).map((node) => getDocs(query(
          collection(db, "nodes", node.id, "bookings"),
          where("extendedProps.latestSleepStudyDate", ">=", startDate),
          where("extendedProps.latestSleepStudyDate", "<=", endDate),
        ))));
        snapshots.forEach((snapshot) => snapshot.docs.forEach((record) => {
          bookings.push({ ...record.data(), id: record.id, sourcePath: record.ref.path });
        }));
      }
      setSummary(summarizePostConsult(bookings, {
        startDate, endDate, customer, mode,
        nodeLabel: nodeId ? selectedNodes[0].nodename : "All accessible nodes",
      }));
    } catch (fetchError) {
      setError(`Unable to load Post Consult Summary: ${fetchError.message || "Please try again."}`);
    } finally {
      busy.current = false;
      setLoading(false);
      setProgress("");
    }
  };

  const exportExcel = async () => {
    if (!summary || busy.current) return;
    busy.current = true;
    setExporting(true);
    try {
      await exportPostConsultWorkbook(summary);
      setError("");
    } catch (exportError) {
      setError(`Unable to export Post Consult Summary: ${exportError.message || "Please try again."}`);
    } finally {
      busy.current = false;
      setExporting(false);
    }
  };

  const changeRange = (setter) => (event) => {
    setter(event.target.value);
    setSummary(null);
    setError("");
  };

  return (
    <section aria-labelledby="post-consult-summary-title" className="col-xl-6 mb-5">
      <b id="post-consult-summary-title">Post Consult Summary</b>
      <ol className="small text-muted mt-3 pl-3">
        <li>Count cases by Post Consult Tech and current status, including Not assigned.</li>
        <li>Count cases by Post Consult Doctor and current status, including Not assigned.</li>
        <li>Show the number and details of overdue cases, including their current status. A case is overdue when more than 45 days have passed since its latest sleep study date.</li>
      </ol>
      <p className="small text-muted">Select cases by their latest sleep study date (inclusive). Cases without HN, Cancel and Failure are excluded. Overdue is checked as of today in Bangkok.</p>
      <details className="small mb-3">
        <summary>Statuses included in the overdue check</summary>
        <p className="mt-2 mb-0">{POST_CONSULT_STATUSES.join(", ")}</p>
      </details>
      <fieldset disabled={loading || exporting}>
        <div className="form-group">
          <label htmlFor="post-consult-mode">Summary mode</label>
          <select id="post-consult-mode" className="form-control" value={mode} onChange={changeRange(setMode)}>
            <option value="short">Short — tech and doctor totals</option>
            <option value="full">Full — totals with status breakdowns</option>
          </select>
          <small className="form-text text-muted">
            Both modes count all statuses except Cancel and Failure. Short shows tech and doctor totals; Full adds current status breakdowns. Only the overdue check is restricted to the eight listed statuses.
          </small>
        </div>
        <div className="form-row">
          <div className="form-group col-sm-6">
            <label htmlFor="post-consult-node">Node</label>
            <select id="post-consult-node" className="form-control" value={nodeId} onChange={(event) => {
              setNodeId(event.target.value); setCustomer(""); setSummary(null); setError("");
            }}>
              <option value="">All accessible nodes</option>
              {nodes.map((node) => <option key={node.id} value={node.id}>{node.nodename}</option>)}
            </select>
          </div>
          <div className="form-group col-sm-6">
            <label htmlFor="post-consult-customer">Customer</label>
            <select id="post-consult-customer" className="form-control" value={customer} onChange={changeRange(setCustomer)}>
              <option value="">All Customers</option>
              {customers.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group col-sm-6">
            <label htmlFor="post-consult-start">Latest sleep study date from</label>
            <input id="post-consult-start" className="form-control" type="date" value={startDate} max={endDate || undefined} onChange={changeRange(setStartDate)} />
          </div>
          <div className="form-group col-sm-6">
            <label htmlFor="post-consult-end">Latest sleep study date to</label>
            <input id="post-consult-end" className="form-control" type="date" value={endDate} min={startDate || undefined} onChange={changeRange(setEndDate)} />
          </div>
        </div>
        <button type="button" className="btn btn-primary mr-2 mb-2" onClick={generate} disabled={!startDate || !endDate || loading}>Generate summary</button>
        <button type="button" className="btn btn-outline-primary mb-2" onClick={exportExcel} disabled={!summary || loading || exporting}>{exporting ? "Preparing Excel..." : "Export Excel"}</button>
      </fieldset>
      {loading && <p role="status" className="small">{progress || "Loading cases..."}</p>}
      {error && <div role="alert" className="alert alert-warning mt-2">{error}</div>}
      {summary && <>
        <p className="mt-3 mb-2"><strong>{summary.total} cases</strong> · <strong className="text-danger">{summary.overdue.length} overdue</strong></p>
        <p className="small text-muted">Study date: {summary.startDate} to {summary.endDate} · Checked: {summary.asOf} (Bangkok). One sleep study order counts as one case.</p>
        <p className="small text-muted">Mode: {summary.mode === "full" ? "Full" : "Short"} · Node: {summary.nodeLabel} · Customer: {summary.customer || "All Customers"}</p>
        {summary.mode === "full" && <><h6>Cases by current status</h6>
        <p className="small text-muted">Use Doctor Scored and Post-Consult counts to track cases through consultation. Counts reflect each case's current status.</p>
        <StatusCounts rows={summary.statuses} /></>}
        <p className="small mb-2"><strong>{summary.assignmentTotal} assignment cases</strong> across all statuses except Cancel and Failure, including Not assigned. These tech and doctor counts are the same in Short and Full.</p>
        <div className="row">
          <AssignmentCounts title="Post Consult Tech" rows={summary.techs} showStatuses={summary.mode === "full"} />
          <AssignmentCounts title="Post Consult Doctor" rows={summary.doctors} showStatuses={summary.mode === "full"} />
        </div>
        <h6>Overdue cases ({summary.overdue.length})</h6>
        {summary.overdue.length ? <div className="table-responsive" style={{ maxHeight: 400 }}>
          <table className="table table-sm table-bordered">
            <thead><tr><th>Case / Patient</th><th>Latest study date</th><th>Current status</th><th>Days since study</th><th>Days overdue</th><th>Post Consult Tech</th><th>Post Consult Doctor</th></tr></thead>
            <tbody>{summary.overdue.map((row) => <tr key={row.key}>
              <td><strong>{row.order || "No order"}</strong><br />HN: {row.hn || "Not assigned"}<br />{row.patient || "No patient name"}<br /><small>{row.node} · {row.customer}</small></td>
              <td className="text-nowrap">{row.studyDate}</td><td>{row.status}</td><td>{row.daysSinceStudy}</td><td>{row.overdueDays}</td><td>{row.tech}</td><td>{row.doctor}</td>
            </tr>)}</tbody>
          </table>
        </div> : <p className="text-muted">No overdue cases in this date range and the listed statuses.</p>}
      </>}
    </section>
  );
};

export default PostConsultSummary;
